import React from 'react';
import { Button, FilterChip } from '@parsina/framework';
import { C } from '../../constants/theme';
import { LTR_TEXT } from '../../i18n/I18nProvider.jsx';
import { QUADRANTS, toothOutline, resolveWithPrimary } from '../../lib/teeth.js';

// THE TOUCH PATH INTO A TOOTH.
//
// ── why this component exists ───────────────────────────────────────────────
//
// The odontogram is 620 viewBox units carrying 32 crowns. Scaled to a 360px
// phone that is ~0.58×, which puts a crown near 10px and one of its five
// surfaces far below that. §6's floor is 2.75rem (44px). Those two numbers
// cannot both be satisfied inside the arch, and the arch is the part that
// cannot move: CORE.md §9 makes the anatomical frame a patient-safety
// invariant, so pan/zoom (a nested scroll region, §6) , a per-quadrant redraw
// (which destroys the whole-mouth glance that makes an odontogram an
// odontogram) and mirroring are all off the table.
//
// So the CHART stays the overview — read at a glance, tappable as a
// convenience — and the compliant target lives outside the <svg>: quadrant
// chips over a grid of ≥2.75rem tooth chips. Both paths call the SAME
// `onPick(fdi)` the chart calls, and surface-level editing (the genuinely
// sub-10px part) happens in the tooth sheet, where every surface is its own
// 44px chip.
//
// ── why the quadrants are in this order ─────────────────────────────────────
//
// SCREEN order, from `QUADRANTS` in lib/teeth.js: Q1 · Q2 on top, Q4 · Q3
// beneath — the patient's right first, in BOTH locales, exactly as the chart
// draws them. Numeric order (1,2,3,4) would zig-zag across the mouth and break
// the dentist's map of the arch between the two controls.

const MobileToothPicker = ({ teeth, byFdi, showPrimary, selected, onPick, t }) => {
  const [quadrant, setQuadrant] = React.useState(QUADRANTS[0].id);
  const withPrimary = resolveWithPrimary(showPrimary, teeth);

  const active = QUADRANTS.find((q) => q.id === quadrant) || QUADRANTS[0];
  const fdis = withPrimary ? [...active.teeth, ...active.primary] : active.teeth;

  return (
    <div data-testid="chart-picker">
      <div
        role="group"
        aria-label={t('chart.mobile.pickTooth')}
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}
      >
        {QUADRANTS.map((q) => (
          <FilterChip
            key={q.id}
            label={t(q.key)}
            active={q.id === active.id}
            onClick={() => setQuadrant(q.id)}
            data-testid={`chart-picker-quadrant-${q.id}`}
            className="w-full justify-center text-[0.6875rem]"
          />
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          // 2.75rem minimum per column, as many as fit — the §6 floor expressed
          // as the layout rule rather than checked after the fact.
          gridTemplateColumns: 'repeat(auto-fit, minmax(2.75rem, 1fr))',
          gap: '0.375rem',
          marginTop: '0.625rem',
        }}
      >
        {fdis.map((fdi) => {
          const state = byFdi?.[fdi];
          const tint = toothOutline(state);
          const on = selected === fdi;
          return (
            <Button
              key={fdi}
              variant="outline"
              onClick={() => onPick?.(fdi)}
              aria-pressed={on}
              aria-label={`${t('chart.tooth')} ${fdi}`}
              data-testid={`chart-picker-tooth-${fdi}`}
              className="min-h-11 min-w-11 px-0"
              style={{
                // The chip carries the chart's OWN colour vocabulary, from the
                // same `toothOutline` the crown outline uses — so a red tooth on
                // the chart is a red chip here and the two cannot drift.
                background: on ? tint.stroke : tint.fill,
                borderColor: tint.stroke,
                color: on ? '#fff' : C.textPrimary,
                fontWeight: 700,
              }}
            >
              <span {...LTR_TEXT}>{fdi}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default MobileToothPicker;
